// src/pages/Registration.tsx
import React, { useState } from 'react';
import { CircleCheck as CheckCircle, Mail, User, Lock } from 'lucide-react';
import { Button } from '../components/Button';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export function Registration() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    if (!name.trim()) { setError('Please enter your name'); setIsSubmitting(false); return; }
    if (!email.trim()) { setError('Please enter your email'); setIsSubmitting(false); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters long'); setIsSubmitting(false); return; }

    try {
      setSuccess('Creating your account...');
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: { name: name.trim() },
        },
      });

      if (signUpError) throw new Error(signUpError.message);
      if (!signUpData?.user) throw new Error('Signup failed: no user returned');

      // Profile is created automatically by the database trigger on_auth_user_created.
      // Update the name explicitly in case the trigger ran before metadata was available.
      await supabase
        .from('profiles')
        .update({ name: name.trim() })
        .eq('id', signUpData.user.id);

      setSuccess('Account created! Redirecting...');
      navigate('/impactintentions');
    } catch (err: any) {
      console.error('🚨 Signup error:', err);
      setError(err.message || 'Something went wrong');
      try {
        await supabase.auth.signOut();
      } catch (signOutError) {
        console.error('Cleanup failed:', signOutError);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-b from-white to-blue-50">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
        <div className="flex items-center gap-3 mb-6">
          <CheckCircle className="w-8 h-8 text-[#318231]" />
          <h1 className="text-3xl font-bold text-[#004060]">Create Your Account</h1>
        </div>

        <p className="text-xl text-gray-600 mb-8">Let's start by setting up your account</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}
          {success && <div className="p-4 bg-green-50 text-green-700 rounded-lg text-sm">{success}</div>}

          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={cn(
                  "w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300",
                  "focus:ring-2 focus:ring-[#236caf] focus:border-transparent",
                  "placeholder:text-gray-400",
                  isSubmitting && "opacity-50 cursor-not-allowed"
                )}
                placeholder="Enter your name"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={cn(
                  "w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300",
                  "focus:ring-2 focus:ring-[#236caf] focus:border-transparent",
                  "placeholder:text-gray-400",
                  isSubmitting && "opacity-50 cursor-not-allowed"
                )}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={cn(
                  "w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300",
                  "focus:ring-2 focus:ring-[#236caf] focus:border-transparent",
                  "placeholder:text-gray-400",
                  isSubmitting && "opacity-50 cursor-not-allowed"
                )}
                placeholder="Choose a password"
                required
                minLength={6}
                disabled={isSubmitting}
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">Must be at least 6 characters long</p>
          </div>

          {/* Submit */}
          <div className="space-y-4">
            <Button
              type="submit"
              disabled={isSubmitting || !name || !email || !password}
              className={cn(
                'w-full',
                (isSubmitting || !name || !email || !password) && 'opacity-50 cursor-not-allowed'
              )}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account'}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="text-[#236caf] hover:text-[#004060] transition-colors duration-200"
              >
                Already have an account? Sign in
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
